import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Calendar, Copy, ArrowRight, Check, AlertCircle, Eye, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminYearlySchedulePage = () => {
  const [availableYears, setAvailableYears] = useState([]);
  const [sourceYear, setSourceYear] = useState('');
  const [targetYear, setTargetYear] = useState('');
  const [preserveWeekday, setPreserveWeekday] = useState(true);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const fetchAvailableYears = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/course-sessions/years-available`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableYears(data.years || []);
        
        // Auto-select the latest year as source
        if (data.years && data.years.length > 0) {
          const latestYear = data.years[data.years.length - 1].year;
          setSourceYear(String(latestYear));
          setTargetYear(String(latestYear + 1));
        }
      }
    } catch (error) {
      toast.error('Failed to load available years');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!sourceYear || !targetYear) {
      toast.error('Please select both source and target years');
      return;
    }

    setPreviewing(true);
    setPreview(null);
    setResult(null);

    try {
      const response = await fetch(`${API}/admin/course-sessions/preview-year`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_year: parseInt(sourceYear),
          target_year: parseInt(targetYear),
          preserve_weekday: preserveWeekday
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPreview(data);
      } else {
        toast.error(data.detail || 'Preview failed');
      }
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    if (!preview) {
      toast.error('Please preview first');
      return;
    }

    setGenerating(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/admin/course-sessions/generate-year`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_year: parseInt(sourceYear),
          target_year: parseInt(targetYear),
          preserve_weekday: preserveWeekday
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        toast.success(`Created ${data.created_count} sessions for ${targetYear}`);
        fetchAvailableYears(); // Refresh years list
      } else {
        toast.error(data.detail || 'Generation failed');
      }
    } catch (error) {
      toast.error('Failed to generate sessions');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-yearly-schedule-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">Yearly Schedule Roll-Forward</h1>
        <p className="text-[#6B7280]">Generate next year's training schedule from an existing year</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Schedule Configuration
            </CardTitle>
            <CardDescription>
              Select source and target years for the roll-forward
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Available Years */}
            <div>
              <Label className="text-sm text-[#6B7280]">Available Years</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableYears.map(y => (
                  <Badge key={y.year} variant="outline" className="px-3 py-1">
                    {y.year}: {y.session_count} sessions
                  </Badge>
                ))}
              </div>
            </div>

            {/* Source Year */}
            <div className="space-y-2">
              <Label>Source Year (copy from)</Label>
              <Select value={sourceYear} onValueChange={setSourceYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source year" />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(y => (
                    <SelectItem key={y.year} value={String(y.year)}>
                      {y.year} ({y.session_count} sessions)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Target Year */}
            <div className="space-y-2">
              <Label>Target Year (create for)</Label>
              <Select value={targetYear} onValueChange={setTargetYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target year" />
                </SelectTrigger>
                <SelectContent>
                  {[2026, 2027, 2028, 2029, 2030].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preserve Weekday */}
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="preserveWeekday" 
                checked={preserveWeekday}
                onCheckedChange={setPreserveWeekday}
              />
              <Label htmlFor="preserveWeekday" className="cursor-pointer">
                Preserve weekday pattern (Mon-Fri courses stay Mon-Fri)
              </Label>
            </div>

            {/* Visual */}
            {sourceYear && targetYear && (
              <div className="flex items-center justify-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#33AADD]">{sourceYear}</div>
                  <div className="text-sm text-[#6B7280]">Source</div>
                </div>
                <ArrowRight className="h-8 w-8 text-[#6B7280]" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{targetYear}</div>
                  <div className="text-sm text-[#6B7280]">Target</div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={handlePreview}
                disabled={previewing || !sourceYear || !targetYear}
                className="flex-1"
              >
                {previewing ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current me-2"></div>Loading...</>
                ) : (
                  <><Eye className="h-4 w-4 me-2" />Preview</>
                )}
              </Button>
              <Button 
                className="btn-primary flex-1"
                onClick={handleGenerate}
                disabled={generating || !preview}
              >
                {generating ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>Generating...</>
                ) : (
                  <><Play className="h-4 w-4 me-2" />Generate {targetYear}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Results */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Copy className="h-5 w-5" />
              {preview ? 'Preview Results' : result ? 'Generation Complete' : 'Preview'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!preview && !result && (
              <div className="text-center py-8 text-[#6B7280]">
                <Eye className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Click "Preview" to see what will be created</p>
              </div>
            )}

            {preview && !result && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{preview.will_create_count}</div>
                    <div className="text-sm text-green-700">Will Create</div>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-yellow-600">{preview.will_skip_count}</div>
                    <div className="text-sm text-yellow-700">Will Skip (duplicates)</div>
                  </div>
                </div>

                {/* By Month */}
                <div>
                  <Label className="text-sm text-[#6B7280]">Sessions by Month</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {Object.entries(preview.months_summary || {}).map(([month, count]) => (
                      <div key={month} className="text-center p-2 bg-gray-50 rounded text-sm">
                        <div className="font-medium">{month.slice(5)}</div>
                        <div className="text-[#6B7280]">{count}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* By City */}
                <div>
                  <Label className="text-sm text-[#6B7280]">Sessions by City</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(preview.cities_summary || {}).slice(0, 10).map(([city, count]) => (
                      <Badge key={city} variant="outline" className="text-xs">
                        {city}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Sample */}
                <div>
                  <Label className="text-sm text-[#6B7280]">Sample Sessions (first 5)</Label>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {(preview.sample_sessions || []).slice(0, 5).map((s, i) => (
                      <div key={i} className="text-xs p-2 bg-gray-50 rounded flex justify-between">
                        <span className="truncate flex-1">{s.template_title}</span>
                        <span className="text-[#6B7280] ms-2">{s.city} • {s.new_date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {result && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 font-medium">
                    <Check className="h-5 w-5" />
                    Generation Complete
                  </div>
                  <p className="text-green-700 mt-2">{result.message}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xl font-bold">{result.source_sessions}</div>
                    <div className="text-xs text-[#6B7280]">Source Sessions</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded">
                    <div className="text-xl font-bold text-green-600">{result.created_count}</div>
                    <div className="text-xs text-green-700">Created</div>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded">
                    <div className="text-xl font-bold text-yellow-600">{result.skipped_count}</div>
                    <div className="text-xs text-yellow-700">Skipped</div>
                  </div>
                </div>

                <p className="text-sm text-[#6B7280]">
                  You can now view and edit the {targetYear} sessions in the Course Sessions page.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <h3 className="font-medium mb-2">How Roll-Forward Works</h3>
          <ul className="text-sm text-[#6B7280] space-y-1">
            <li>• Sessions from the source year are copied to the target year with adjusted dates</li>
            <li>• If "Preserve weekday pattern" is enabled, Monday courses stay on Mondays</li>
            <li>• Duplicate sessions (same course, city, date) are automatically skipped</li>
            <li>• Prices, cities, and delivery modes are preserved from the source</li>
            <li>• After generation, you can manually add, edit, or remove sessions</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminYearlySchedulePage;
